import { differenceInCalendarDays } from "date-fns";
import type { ScheduleNode } from "../types.ts";
import { parseScheduleDate } from "./gantt.ts";
import { leafDescendants } from "./tree.ts";

export interface DelayInfo {
	kind: "none" | "delayed" | "finished_late";
	days: number;
}

const NONE: DelayInfo = { kind: "none", days: 0 };

function daysBetween(later: string, earlier: string): number {
	const a = parseScheduleDate(later);
	const b = parseScheduleDate(earlier);
	if (!a || !b) return 0;
	return differenceInCalendarDays(a, b);
}

export function classifyDelay(node: ScheduleNode, today: string): DelayInfo {
	if (!node.end) return NONE;
	if (node.actualEnd) {
		return node.actualEnd > node.end
			? { kind: "finished_late", days: daysBetween(node.actualEnd, node.end) }
			: NONE;
	}
	if (node.status === "completed" || node.status === "cancelled") return NONE;
	if (node.end < today) {
		return { kind: "delayed", days: daysBetween(today, node.end) };
	}
	return NONE;
}

export function delayByStage(
	nodes: ScheduleNode[],
	today: string,
	counts: Map<string, number>,
): Map<string, DelayInfo> {
	const infos = new Map<string, DelayInfo>();
	nodes.forEach((node, index) => {
		if ((counts.get(node.id) ?? 0) === 0) {
			infos.set(node.id, classifyDelay(node, today));
			return;
		}
		let days = 0;
		for (const leaf of leafDescendants(nodes, index, counts)) {
			const info = classifyDelay(leaf, today);
			if (info.kind === "delayed") days = Math.max(days, info.days);
		}
		infos.set(node.id, days > 0 ? { kind: "delayed", days } : NONE);
	});
	return infos;
}
